import { KeyRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { EmptyState } from "@/components/ui/feedback";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { WipHint, WIP_ACTION_HINT } from "@/components/ui/wip-hint";

/**
 * RCV-03 — Recovery Approval Ceremony.
 *
 * Layout ported from `.design-ref/prototype/app/(enterprise-wallet)/recovery/approval/
 * page.tsx` → `features/recovery/recovery-approval.tsx` (`RecoveryApproval`): the
 * header with its status slot, the two side-by-side panels ("Request Summary",
 * "Custodian Approvals"), the quorum note, and the reject/approve action row.
 *
 * No data and no behaviour are ported. The design read a fictional request
 * (`RECOVERY_REQUEST_2024_001`), a fictional approver roster
 * (`RECOVERY_APPROVALS_2024_001`) and a fixed quorum note
 * (`RECOVERY_APPROVAL_QUORUM_NOTE`) straight out of `data/mock.ts`, gated the
 * actions on a client-side permission hook, and drove a local
 * `pending → approved` state machine that ended in a "Recovery Process Initiated"
 * success card. There is no recovery backend in this environment
 * (page-delivery-pipeline.md G2 `defer`; BOARD wave 4), and this is a
 * custody-adjacent four-eyes ceremony, so **no approver, decision or approval state
 * may be rendered**.
 *
 * Consequences, deliberately taken:
 *   - the design's `Recovery Approval — {request id}` heading loses the id, because
 *     the id is data;
 *   - the design's `warning` "Pending Approval" badge becomes the neutral
 *     not-available marker: "pending approval" is itself an approval state, and there
 *     is no request in this environment to be pending;
 *   - the design's approved branch (the "Recovery Process Initiated" card, its
 *     "Begin Ceremony →" link and its "← Back to Key Recovery" link) is not rendered
 *     at all — it is only reachable once a real quorum exists;
 *   - the reject confirmation `Modal` is not rendered, because confirming it mutates
 *     a request and navigates on success.
 *
 * Every control that would need the missing backend is `disabled`, carries
 * `WIP_ACTION_HINT` through `title` and `aria-describedby`, and has no handler that
 * could report success. The page calls no endpoint (`src/lib/api.ts` is not imported).
 */

/** Id of the paragraph that states, in plain text, why the controls are inert. */
const ACTION_HINT_ID = "recovery-approval-action-hint";

/** The design's `Row` helper: a `dt`/`dd` pair inside the summary `dl`. */
function Row({ k }: { k: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-muted">{k}</dt>
      <dd className="text-right font-medium text-ink">
        <UnavailableValue />
      </dd>
    </div>
  );
}

export default function RecoveryApprovalPage() {
  return (
    <div data-testid="recovery-approval-page">
      <Breadcrumb items={[{ label: "Recovery", href: "/recovery" }, { label: "Approval" }]} />

      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[24px] font-bold leading-tight tracking-tight text-ink">
              Recovery Approval
            </h1>
            <WipHint />
          </div>
          <p className="mt-1 text-sm text-muted" data-testid="wip-note">
            This area has no backend in this environment yet.
          </p>
        </div>
        {/* The design's status slot. It held a `warning` "Pending Approval" badge —
            an approval state — so it carries the neutral not-available marker
            instead. */}
        <Badge variant="neutral" size="sm">
          Not available yet
        </Badge>
      </div>

      {/* The deferred page's own honest statement, kept verbatim: this is the whole
          ceremony, not just one panel, so it is stated once at the top as a status
          region rather than left to the panels to imply. */}
      <div
        role="status"
        className="mb-5 rounded-[10px] border border-line bg-surface p-5"
        data-testid="approval-unavailable"
      >
        <p className="text-sm font-bold text-ink">
          Recovery approval is not available in this environment yet
        </p>
        <p className="mt-1 text-sm leading-relaxed text-muted">
          Four-eyes approval of a recovery request has no backend in this environment yet. No approver,
          decision, or approval state shown here is real.
        </p>
      </div>

      <div className="mb-5 grid gap-4 lg:grid-cols-2">
        <div className="rounded-[10px] border border-line bg-surface p-6" data-testid="request-summary">
          <div className="mb-4 text-base font-bold text-ink">Request Summary</div>
          {/* The design's row labels are structure; every value is data and shows the
              shared not-available marker rather than a fabricated request. */}
          <dl className="space-y-2.5 text-sm">
            <Row k="Type" />
            <Row k="Reason" />
            <Row k="Affected Wallet" />
            <Row k="Urgency" />
            <Row k="Requester" />
            <Row k="Submitted" />
          </dl>
        </div>

        <div className="rounded-[10px] border border-line bg-surface p-6" data-testid="custodian-approvals">
          <div className="mb-4 text-base font-bold text-ink">Custodian Approvals</div>

          {/* The design rendered one row per approver from
              `RECOVERY_APPROVALS_2024_001` — fictional names, roles and decisions.
              With no backend there is no roster and no decision, so the designed
              empty state stands in. */}
          <EmptyState
            className="px-0 pt-0 pb-6"
            icon={<KeyRound aria-hidden className="text-muted" />}
            title="No custodian approvals to show yet"
            description="Custodian approvals for a recovery request come from the recovery service, which does not exist in this environment yet."
          />

          {/* The design's quorum note was a fixed constant; it stated how many
              approvals a quorum needs, which is a real policy this page cannot
              source. */}
          <div className="mt-4 rounded-md border border-line bg-bg p-3 text-xs text-muted">
            Quorum required to approve: <UnavailableValue />
          </div>
        </div>
      </div>

      <div className="flex gap-2.5">
        <Button variant="destructive" disabled title={WIP_ACTION_HINT} aria-describedby={ACTION_HINT_ID}>
          ✗ Reject Request
        </Button>
        <Button variant="success" disabled title={WIP_ACTION_HINT} aria-describedby={ACTION_HINT_ID}>
          ✓ Approve Request
        </Button>
      </div>

      {/* Why both actions are inert, stated in plain text and referenced by each
          control's `aria-describedby`. */}
      <p id={ACTION_HINT_ID} className="mt-4 text-xs text-muted" data-testid="wip-action-hint">
        {WIP_ACTION_HINT}. No approval or rejection is recorded anywhere.
      </p>
    </div>
  );
}
